import React, { useCallback, useEffect, useState } from 'react';
import api from '../services/api';

interface Turma { id: number; nome: string; }
interface Aluno { id: number; nome: string; }

/**
 * Protocolo de leitura de um comunicado.
 *
 * `total` e `percentual` são `null` quando não há destinatário apurável — a
 * tela mostra "—", nunca 0%. Zero significa "medi e ninguém leu"; null
 * significa "não dá para medir", e são coisas diferentes para quem decide se
 * precisa ligar para as famílias.
 */
interface Leitura {
  lidos: number;
  total: number | null;
  percentual: number | null;
  naoLeram: string[];
}

interface ItemMural {
  id: number;
  tipo: string;
  titulo?: string;
  mensagem?: string;
  criado_em?: string;
  encerra_em?: string | null;
  destino: string;
  leitura: Leitura;
}

interface Apuracao {
  opcoes: Array<{ id: number; texto: string; votos: number; percentual: number | null }>;
  totalVotos: number;
  totalElegiveis: number | null;
  semResposta: string[];
  participacao: number | null;
  encerrada: boolean;
}

const TIPOS = [
  { valor: 'comunicado', label: 'Comunicado' },
  { valor: 'aviso', label: 'Aviso' },
  { valor: 'mensagem', label: 'Mensagem' },
  { valor: 'autorizacao', label: 'Autorização' },
  { valor: 'enquete', label: 'Enquete' },
];

const dataBR = (s?: string | null) => (s ? new Date(s).toLocaleString('pt-BR') : '');

/**
 * "—" quando não é mensurável (ver o comentário de `Leitura`), e vírgula
 * decimal: "16.7%" é número em inglês numa tela em português.
 */
const pct = (v: number | null) =>
  v === null || v === undefined ? '—' : `${v.toLocaleString('pt-BR')}%`;

/** "1 família", "2 famílias" — a tela não fala "1 famílias". */
const familias = (n: number) => (n === 1 ? '1 família' : `${n} famílias`);

export default function ComunicacaoPage() {
  const [turmas, setTurmas] = useState<Turma[]>([]);
  const [alunos, setAlunos] = useState<Aluno[]>([]);
  const [mural, setMural] = useState<ItemMural[]>([]);

  const [tipo, setTipo] = useState('comunicado');
  const [titulo, setTitulo] = useState('');
  const [mensagem, setMensagem] = useState('');
  const [destino, setDestino] = useState<'todos' | 'turma' | 'aluno'>('todos');
  const [idTurma, setIdTurma] = useState(0);
  const [idAluno, setIdAluno] = useState(0);
  const [opcoes, setOpcoes] = useState<string[]>(['', '']);
  const [encerraEm, setEncerraEm] = useState('');

  const [enviando, setEnviando] = useState(false);
  const [msg, setMsg] = useState('');
  const [erro, setErro] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('');
  const [detalhe, setDetalhe] = useState<number | null>(null);
  const [apuracoes, setApuracoes] = useState<Record<number, Apuracao>>({});

  const carregarMural = useCallback(async () => {
    try {
      const q = filtroTipo ? `?tipo=${filtroTipo}` : '';
      const { data } = await api.get<ItemMural[]>(`/comunicacao/mural${q}`);
      setMural(Array.isArray(data) ? data : []);
    } catch { /* silencioso */ }
  }, [filtroTipo]);

  useEffect(() => {
    api.get<Turma[]>('/turmas').then(({ data }) => setTurmas(Array.isArray(data) ? data : [])).catch(() => {});
    api.get<Aluno[]>('/alunos').then(({ data }) => setAlunos(Array.isArray(data) ? data : [])).catch(() => {});
  }, []);
  useEffect(() => { carregarMural(); }, [carregarMural]);

  const enviar = async () => {
    if (!titulo.trim() && !mensagem.trim()) { setErro('Informe um título ou uma mensagem.'); return; }
    if (destino === 'turma' && !idTurma) { setErro('Selecione a turma.'); return; }
    if (destino === 'aluno' && !idAluno) { setErro('Selecione a criança.'); return; }
    const opcoesLimpas = opcoes.map((o) => o.trim()).filter(Boolean);
    if (tipo === 'enquete' && opcoesLimpas.length < 2) {
      setErro('Uma enquete precisa de pelo menos duas opções de resposta.');
      return;
    }
    setEnviando(true); setErro(''); setMsg('');
    try {
      const { data } = await api.post('/comunicacao/enviar', {
        tipo, titulo, mensagem, destino, id_turma: idTurma, id_aluno: idAluno,
        opcoes: tipo === 'enquete' ? opcoesLimpas : undefined,
        encerra_em: tipo === 'enquete' && encerraEm ? encerraEm : undefined,
      });
      setMsg(
        data?.termos_gerados
          ? `✅ Autorização enviada — ${data.termos_gerados} termo(s) aguardando assinatura das famílias.`
          : '✅ Comunicado enviado às famílias!',
      );
      setTitulo(''); setMensagem(''); setOpcoes(['', '']); setEncerraEm('');
      carregarMural();
      setTimeout(() => setMsg(''), 5000);
    } catch (e: any) {
      setErro(e?.response?.data?.message || 'Falha ao enviar.');
    } finally { setEnviando(false); }
  };

  const excluir = async (id: number) => {
    try {
      await api.delete(`/comunicacao/${id}`);
      setMural((m) => m.filter((i) => i.id !== id));
    } catch { /* silencioso */ }
  };

  const verDetalhe = async (item: ItemMural) => {
    const abrindo = detalhe === item.id ? null : item.id;
    setDetalhe(abrindo);
    if (abrindo && item.tipo === 'enquete' && !apuracoes[item.id]) {
      try {
        const { data } = await api.get<Apuracao>(`/comunicacao/${item.id}/apuracao`);
        setApuracoes((a) => ({ ...a, [item.id]: data }));
      } catch { /* silencioso */ }
    }
  };

  return (
    <div className="stack-cadastros" style={{ maxWidth: 960 }}>
      {/* ── ENVIAR ── */}
      <section style={{ background: '#fff', border: '1px solid #eee', borderRadius: 12, padding: 16 }}>
        <h3 style={{ marginTop: 0 }}>📣 Enviar comunicado às famílias</h3>
        {erro && <div style={{ color: '#c62828', marginBottom: 8 }}>{erro}</div>}
        {msg && <div style={{ color: '#2e7d32', marginBottom: 8 }}>{msg}</div>}

        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 10 }}>
          <label>Tipo
            <select value={tipo} onChange={(e) => setTipo(e.target.value)} style={{ display: 'block' }}>
              {TIPOS.map((t) => <option key={t.valor} value={t.valor}>{t.label}</option>)}
            </select>
          </label>
          <label style={{ flex: '1 1 260px' }}>Título
            <input value={titulo} onChange={(e) => setTitulo(e.target.value)} style={{ display: 'block', width: '100%' }} placeholder="Ex.: Reunião de pais" />
          </label>
        </div>

        <label style={{ display: 'block', marginBottom: 10 }}>Mensagem
          <textarea value={mensagem} onChange={(e) => setMensagem(e.target.value)} rows={3} style={{ display: 'block', width: '100%' }} placeholder="Escreva o comunicado..." />
        </label>

        {tipo === 'enquete' && (
          <div style={{ background: '#f7f9ff', border: '1px solid #e3e9f7', borderRadius: 10, padding: 12, marginBottom: 10 }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Opções de resposta</div>
            {opcoes.map((o, i) => (
              <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                <input
                  value={o}
                  onChange={(e) => setOpcoes((os) => os.map((v, j) => (j === i ? e.target.value : v)))}
                  placeholder={`Opção ${i + 1}`}
                  style={{ flex: 1 }}
                />
                {opcoes.length > 2 && (
                  <button onClick={() => setOpcoes((os) => os.filter((_, j) => j !== i))} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#c62828' }}>✕</button>
                )}
              </div>
            ))}
            <button onClick={() => setOpcoes((os) => [...os, ''])} style={{ fontSize: 13, padding: '4px 10px', borderRadius: 6, border: '1px solid #ddd', background: '#fff', cursor: 'pointer' }}>+ opção</button>
            <label style={{ display: 'block', marginTop: 10, fontSize: 13 }}>Encerrar em (opcional)
              <input type="datetime-local" value={encerraEm} onChange={(e) => setEncerraEm(e.target.value)} style={{ display: 'block' }} />
              <span style={{ color: '#777', fontSize: 12 }}>Sem data, a enquete fica aberta até você encerrar.</span>
            </label>
          </div>
        )}

        {tipo === 'autorizacao' && (
          <div style={{ background: '#fff8e1', border: '1px solid #ffe0a3', borderRadius: 10, padding: 12, marginBottom: 10, fontSize: 13 }}>
            ✍️ Isto gera um <strong>termo para cada criança do destino</strong>, que a família assina no Portal
            dos Pais com nome, CPF e registro de data/hora — e não um aviso comum. Acompanhe as assinaturas
            em <strong>Secretaria &rsaquo; Termos</strong>.
          </div>
        )}

        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'end', marginBottom: 4 }}>
          <div>
            <div style={{ fontSize: 13, color: '#555', marginBottom: 4 }}>Destinatário</div>
            <div style={{ display: 'flex', gap: 12 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}><input type="radio" checked={destino === 'todos'} onChange={() => setDestino('todos')} /> Todos</label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}><input type="radio" checked={destino === 'turma'} onChange={() => setDestino('turma')} /> Turma</label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}><input type="radio" checked={destino === 'aluno'} onChange={() => setDestino('aluno')} /> Criança</label>
            </div>
          </div>
          {destino === 'turma' && (
            <label>Turma
              <select value={idTurma} onChange={(e) => setIdTurma(Number(e.target.value))} style={{ display: 'block' }}>
                <option value={0}>Selecione...</option>
                {turmas.map((t) => <option key={t.id} value={t.id}>{t.nome}</option>)}
              </select>
            </label>
          )}
          {destino === 'aluno' && (
            <label>Criança
              <select value={idAluno} onChange={(e) => setIdAluno(Number(e.target.value))} style={{ display: 'block' }}>
                <option value={0}>Selecione...</option>
                {alunos.map((a) => <option key={a.id} value={a.id}>{a.nome}</option>)}
              </select>
            </label>
          )}
          <button onClick={enviar} disabled={enviando} style={{ padding: '10px 22px', borderRadius: 8, border: 'none', background: enviando ? '#88a9d8' : '#1565c0', color: '#fff', fontWeight: 700, cursor: enviando ? 'default' : 'pointer' }}>
            {enviando ? 'Enviando…' : '📤 Enviar'}
          </button>
        </div>
      </section>

      {/* ── MURAL ── */}
      <section style={{ background: '#fff', border: '1px solid #eee', borderRadius: 12, padding: 16, marginTop: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
          <h3 style={{ margin: 0 }}>🧱 Mural de comunicados</h3>
          <label style={{ fontSize: 13 }}>Filtrar por tipo{' '}
            <select value={filtroTipo} onChange={(e) => setFiltroTipo(e.target.value)}>
              <option value="">Todos</option>
              {TIPOS.map((t) => <option key={t.valor} value={t.valor}>{t.label}</option>)}
            </select>
          </label>
        </div>

        {mural.length === 0 ? (
          <div style={{ color: '#888', marginTop: 12 }}>Nenhum comunicado ainda.</div>
        ) : (
          <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {mural.map((c) => {
              const leitura = c.leitura || { lidos: 0, total: null, percentual: null, naoLeram: [] };
              const aberto = detalhe === c.id;
              const apuracao = apuracoes[c.id];
              return (
                <div key={c.id} style={{ border: '1px solid #f0f0f0', borderRadius: 10, padding: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      <span className={`badge badge-${String(c.tipo).toLowerCase()}`}>{c.tipo}</span>
                      <strong>{c.titulo || '(sem título)'}</strong>
                      <span style={{ fontSize: 12, background: '#eef2ff', color: '#3949ab', borderRadius: 8, padding: '1px 8px' }}>{c.destino}</span>
                    </div>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                      <span style={{ fontSize: 12, color: '#999' }}>{dataBR(c.criado_em)}</span>
                      <button onClick={() => excluir(c.id)} title="Excluir" style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#c62828' }}>🗑️</button>
                    </div>
                  </div>
                  {c.mensagem && <div style={{ fontSize: 14, marginTop: 6, whiteSpace: 'pre-wrap' }}>{c.mensagem}</div>}

                  {/* Protocolo de leitura: a conta é por FAMÍLIA. */}
                  <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 12, color: '#555' }}>
                      👁️ {leitura.total === null
                        ? 'Sem destinatários para apurar'
                        : `Lido por ${leitura.lidos} de ${familias(leitura.total)} (${pct(leitura.percentual)})`}
                    </span>
                    <button
                      onClick={() => verDetalhe(c)}
                      style={{ fontSize: 12, padding: '2px 10px', borderRadius: 6, border: '1px solid #ddd', background: '#fafafa', cursor: 'pointer' }}
                    >
                      {aberto ? 'Ocultar' : c.tipo === 'enquete' ? 'Ver resultado' : 'Quem falta'}
                    </button>
                  </div>

                  {aberto && (
                    <div style={{ marginTop: 8, background: '#fafafa', borderRadius: 8, padding: 10, fontSize: 13 }}>
                      {c.tipo === 'enquete' && apuracao && (
                        <div style={{ marginBottom: 10 }}>
                          <div style={{ fontWeight: 600, marginBottom: 6 }}>
                            Resultado {apuracao.encerrada ? '(encerrada)' : '(em aberto)'}
                          </div>
                          {apuracao.opcoes.map((o) => (
                            <div key={o.id} style={{ marginBottom: 6 }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span>{o.texto}</span>
                                {/* Separador "·" e não "—": o traço já é o
                                    símbolo de "não mensurável" no percentual, e
                                    "0 voto(s) — —" vira dois traços seguidos. */}
                                <span>{o.votos === 1 ? '1 voto' : `${o.votos} votos`} · {pct(o.percentual)}</span>
                              </div>
                              <div style={{ height: 6, background: '#e8e8e8', borderRadius: 4 }}>
                                <div style={{ width: `${o.percentual ?? 0}%`, height: '100%', background: '#1565c0', borderRadius: 4 }} />
                              </div>
                            </div>
                          ))}
                          <div style={{ color: '#555', marginTop: 6 }}>
                            Participação: {pct(apuracao.participacao)}
                            {apuracao.totalElegiveis !== null && ` (${apuracao.totalVotos} de ${familias(apuracao.totalElegiveis)})`}
                          </div>
                          {apuracao.semResposta.length > 0 && (
                            <div style={{ color: '#8a6d3b', marginTop: 4 }}>
                              Ainda não responderam: {apuracao.semResposta.join(', ')}
                            </div>
                          )}
                        </div>
                      )}
                      {leitura.naoLeram.length > 0 ? (
                        <div style={{ color: '#8a6d3b' }}>
                          Ainda não abriram: {leitura.naoLeram.join(', ')}
                        </div>
                      ) : (
                        <div style={{ color: '#2e7d32' }}>
                          {leitura.total === null ? 'Nada a apurar.' : 'Todas as famílias abriram este comunicado.'}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
